<template>
  <div class="d-flex flex-column vh-100">
    <!-- Sticky Header -->
    <header v-if="!isLoginPage && !isForgotPassword" class="d-flex justify-content-between align-items-center text-black position-fixed w-100 top-0" style="height: 50px; z-index: 1030">
      <div class="d-flex" style="width: 250px;">
        <button class="btn" style="color: white; border-radius: 0px; width: 50px; background-color: #0094de; padding: 0">
          <img :src="menuIcon" alt="Menu" style="vertical-align: middle" @click="toggleSidebar" />
        </button>
        <div v-if="navBarTitleColor" :style="navbarTitleStyle" class="fw-bold flex-fill navbar-Title" style="text-align: center">Datascan Delivery</div>
      </div>
      <div v-if="user" class="dropdown" style="margin-right: 20px">
        <button class="btn dropdown-toggle p-0" type="button" id="userDropdown" data-bs-toggle="dropdown" aria-expanded="false" style="color: black">Hi, {{ user }}</button>
        <ul class="dropdown-menu dropdown-menu-end dropdown-menu-with-arrow" style="border: none; box-shadow: 10px 10px 10px 10px gainsboro; top: 10px" aria-labelledby="userDropdown">
          <div class="dropdown-arrow"></div>
          <li><a class="dropdown-item" style="color: red" href="#" @click.prevent="logout">Log Out</a></li>
        </ul>
      </div>
    </header>

    <!-- Content area with fixed top margin to account for header -->
    <div class="d-flex flex-grow-1" style="margin-top: 50px">
      <!-- Sticky Sidebar -->
      <aside v-if="!isLoginPage && !isForgotPassword" :class="['sidebar bg-dark text-white p-0 d-flex flex-column position-fixed', { collapsed: !showSidebar }]" style="top: 50px; height: calc(100vh - 50px); z-index: 1020">
        <div class="flex-grow-1 d-flex flex-column" style="background-color: #0094de; width: 250px">
          <ul v-if="user" :class="['nav flex-column mt-4', { 'text-center': !showSidebar }]">
            <router-link v-for="route in routes" :key="route.to" v-slot="{ isActive, navigate }" :to="route.to" custom>
              <li class="nav-item mb-3" @click="navigate">
                <span class="nav-link text-white d-flex align-items-center" :class="{ active: isActive, 'justify-content-center': !showSidebar }">
                  <img :src="getIconForRoute(route.title, isActive)" :alt="route.title" class="nav-icon" />
                  <span v-if="showSidebar" class="ms-2">{{ route.title }}</span>
                </span>
              </li>
            </router-link>
          </ul>
        </div>
      </aside>

      <!-- Main content with left margin to account for sidebar -->
      <main class="d-flex flex-column p-3 overflow-auto position-relative w-100" :style="mainContentStyle">
        <router-view class="d-flex flex-column flex-fill" />
        <hr v-if="!isLoginPage" class="mt-3" />
        <footer v-if="!isLoginPage" class="text-center pt-2">
          <small>&copy; 2025 - Datascan Delivery Portal</small>
        </footer>
        <div v-if="shouldShowMessagingPopup" class="chat-popup">
          <messaging-popup />
        </div>
      </main>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, computed, provide, onMounted } from "vue"
import { useRoute, useRouter } from "vue-router"
import { Role, useAuthStore } from "./stores/auth"
import MessagingPopup from "./components/MessagePopup/MessagingPopup.vue"

// Icons
import menuIcon from "@/assets/icons/menu.png"

// Import both white and green icons
// Dashboard icons
import dashboardWhite from "@/assets/icons/HomeWhite.png"
import dashboardGreen from "@/assets/icons/DashboardBlue.png"

// Store icons
import storesWhite from "@/assets/icons/StoreWhite.png"
import storesGreen from "@/assets/icons/StoresBlue.png"

// User icons
import userWhite from "@/assets/icons/UserWhite.png"
import userGreen from "@/assets/icons/UsersBlue.png"

// Driver icons
import driversWhite from "@/assets/icons/DriverWhite.png"
import driversGreen from "@/assets/icons/DriversBlue.png"

// Delivery icons
import deliveriesWhite from "@/assets/icons/DeliveryWhite.png"
import deliveriesGreen from "@/assets/icons/DeliveriesBlue.png"

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()

const showSidebar = ref(true)
const toggleSidebar = () => {
  showSidebar.value = !showSidebar.value
}

// Updated to account for fixed sidebar
const mainContentStyle = computed(() => {
  const shouldShowSidebar = !isLoginPage.value && !isForgotPassword.value && showSidebar.value
  return {
    marginLeft: shouldShowSidebar ? "250px" : "0px",
    marginRight: "0px",
    transition: "margin-left 0.3s ease-in-out"
  }
})

const currentUserRole = computed(() => auth.userInfo?.role ?? Role.None)
const user = computed(() => (auth.isAuthenticated() ? auth.userInfo?.name : undefined))
const isLoginPage = computed(() => route.name === "Login")
const isForgotPassword = computed(() => route.name === "ForgotPassword")
// Unified route list (Dashboard included)
const routes = computed(() => {
  const list = [{ to: "/", title: "Dashboard" }]
  if (currentUserRole.value === Role.Admin) {
    list.push({ to: "/stores", title: "Stores" })
  }
  if (currentUserRole.value !== Role.Driver) {
    list.push({ to: "/users", title: "Users" })
    list.push({ to: "/drivers", title: "Drivers" })
  }
  list.push({ to: "/deliveries", title: "Deliveries" })
  return list
})

const getIconForRoute = (routeTitle: string, isActive: boolean) => {
  switch (routeTitle) {
    case "Dashboard":
      return isActive ? dashboardGreen : dashboardWhite
    case "Stores":
      return isActive ? storesGreen : storesWhite
    case "Users":
      return isActive ? userGreen : userWhite
    case "Drivers":
      return isActive ? driversGreen : driversWhite
    case "Deliveries":
      return isActive ? deliveriesGreen : deliveriesWhite
    default:
      return isActive ? dashboardGreen : dashboardWhite
  }
}

function logout() {
  auth.logout()
}

provide("setQuery", setQuery)

async function setQuery(key: string, value?: string) {
  const query = route.query
  return router.replace({ query: { ...query, [key]: value } })
}

// Ensure dropdown initializes properly
onMounted(() => {
  const dropdownToggle = document.getElementById("userDropdown")
  if (dropdownToggle) {
    new bootstrap.Dropdown(dropdownToggle)
  }
})

const navBarTitleColor = computed(() => true) // Always enable the title styling
const navbarTitleStyle = computed(() => ({
  color: showSidebar.value ? "white" : "#0094DE", // White text when sidebar is open
  backgroundColor: showSidebar.value ? "#0094DE" : "white" // #0094DE background when sidebar is open
}))

// Computed property to determine if the MessagingPopup should be displayed
const shouldShowMessagingPopup = computed(() => {
  return currentUserRole.value === Role.Manager || currentUserRole.value === Role.Driver
})
</script>
<style scoped>
html,
body {
  height: 100%;
  margin: 0;
}

#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  color: #2c3e50;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.sidebar {
  background-color: #0094de;
  max-width: 250px;
  overflow-y: auto;
  transition: width 0.3s ease-in-out;
}

.sidebar.collapsed {
  width: 0px;
  overflow: hidden;
}

.nav-link {
  padding: 10px 15px;
  transition: all 0.3s ease;
  border-top-left-radius: 20px;
  border-bottom-left-radius: 20px;
}

.nav-link:hover {
  cursor: pointer;
  background-color: rgba(255, 255, 255, 0.1);
}

.nav-link.active {
  background-color: white;
  color: #0094de !important;
  font-weight: bold;
  border-top-left-radius: 20px;
  border-bottom-left-radius: 20px;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  padding-left: 15px;
}

.nav-link.active .nav-icon,
.nav-link.active i.bi {
  color: #0094de;
}

.nav-icon {
  width: 24px;
  height: 24px;
  object-fit: contain;
  border: none;
}

.chat-popup {
  position: fixed;
  bottom: 100px;
  margin-right: 100px;
  z-index: 1060;
}

.sidebar:not(.collapsed) .nav {
  padding-left: 20px;
}

.sidebar.collapsed .nav-item {
  display: flex;
  justify-content: center;
  width: 100%;
}

.sidebar.collapsed .nav-link {
  padding: 0.5rem;
}

.sidebar.collapsed .header-container {
  justify-content: center;
  padding: 0 !important;
}

.sidebar.collapsed .nav-link.active {
  border-radius: 50%;
  padding: 0.5rem;
  margin: 0 auto;
  background-color: white;
}

.navbar-Title {
  margin-left: 0px;
  text-align: center;
  align-items: center;
  align-self: center;
  padding: 14px 0rem;
}

.dropdown-menu-with-arrow {
  position: relative;
  margin-top: 10px !important;
}

.dropdown-arrow {
  position: absolute;
  top: -10px;
  right: 0px;
  width: 0;
  height: 0;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-bottom: 10px solid white;
  filter: drop-shadow(0 -2px 2px rgba(0, 0, 0, 0.1));
}

@import url("https://cdn.jsdelivr.net/npm/bootstrap-icons@1.5.0/font/bootstrap-icons.css");
</style>
